'use client';

import { CircularProgress, Stack, Typography } from '@mui/material';
import type { Notebook } from '@repo/api-gw-sdk';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { dayjs } from '@/utils/dayjs';

import { DeleteNotebookDialog } from './deleteNotebookDialog';

interface NotebookPanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const NotebookPanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: NotebookPanelProps) => {
  const dal = useDAL();
  const router = useRouter();
  const { isAuthorized } = useRoles();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: notebooksData, isLoading: isNotebooksLoading } =
    dal.lakehouse.useListNotebooks(workspaceId);

  const notebook = notebooksData?.notebooks?.find(
    (nb: Notebook) => nb.id === id
  );

  if (isNotebooksLoading || !notebook) {
    return (
      <PanelWrapper
        header={{
          title: 'Notebook Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canDelete = isAuthorized('delete:notebook');

  const handleOpen = () => {
    router.push(`/lakehouse/notebooks/${notebook.id}`);
  };

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Notebook Details',
          onClose,
          actions: (
            <>
              <ButtonWithIcon
                variant='outlined'
                size='small'
                onClick={handleOpen}
                text='Open in Editor'
                icon='material-symbols-edit-outline-rounded'
              />
              <OptionMenu
                options={[
                  {
                    id: 'delete-notebook',
                    text: 'Delete',
                    icon: 'material-symbols-delete-outline-rounded',
                    menuItemProps: {
                      disabled: !canDelete,
                      onClick: () => setDeleteDialogOpen(true),
                    },
                  },
                ]}
              />
            </>
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px'>
          {/* Notebook Details */}
          <Stack gap='12px'>
            <DetailRow label='ID' value={notebook.id} />
            <DetailRow label='Name' value={notebook.name} />
            <DetailRow
              label='Description'
              value={notebook.description ?? '-'}
            />
            <DetailRow label='Created By' value={notebook.createdBy || '-'} />
            <DetailRow
              label='Created'
              value={dayjs
                .utc(notebook.createdAt)
                .format('MMM D, YYYY HH:mm:ss')}
            />
            <DetailRow
              label='Last Updated'
              value={
                notebook.updatedAt
                  ? dayjs.utc(notebook.updatedAt).format('MMM D, YYYY HH:mm:ss')
                  : '-'
              }
            />
          </Stack>
        </Stack>
      </PanelWrapper>

      <DeleteNotebookDialog
        notebook={notebook}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '100px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
